import type { ComponentProps } from 'react';
import { ChevronDown } from 'lucide-react';
import { cx } from './cx';
import { useFieldControl } from './field-context';
import { controlClass } from './control-styles';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<ComponentProps<'select'>, 'size'> {
  options?: readonly SelectOption[];
  /** Grouped options (<optgroup>). */
  groups?: readonly { label: string; options: readonly SelectOption[] }[];
  /** First, empty option, e.g. "(select)". Selected when value is ''. */
  placeholder?: string;
  invalid?: boolean;
  valid?: boolean;
}

/**
 * Native <select>, styled like Ace .form-control. Native keeps full keyboard, touch and screen-
 * reader support on every platform (the legacy UI used plain selects too).
 */
export function Select({
  options,
  groups,
  placeholder,
  invalid,
  valid,
  className,
  children,
  ...props
}: SelectProps) {
  const f = useFieldControl({ ...props, invalid, valid });
  return (
    <span className="relative block">
      <select
        {...props}
        id={f.id}
        aria-describedby={f['aria-describedby']}
        aria-invalid={f['aria-invalid']}
        required={f.required}
        disabled={f.disabled}
        className={cx(
          controlClass(f.invalid, f.valid),
          'h-control appearance-none pe-8',
          className,
        )}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options?.map((o) => (
          <option key={o.value} value={o.value} disabled={o.disabled}>
            {o.label}
          </option>
        ))}
        {groups?.map((g) => (
          <optgroup key={g.label} label={g.label}>
            {g.options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ))}
        {children}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute end-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-muted"
      />
    </span>
  );
}
